import React, { useRef } from 'react'
import { cloneElement, useMemo, useState } from 'react'
import {
  Placement,
  offset,
  flip,
  shift,
  autoUpdate,
  useFloating,
  useInteractions,
  useHover,
  useFocus,
  useRole,
  useDismiss,
  FloatingArrow,
} from '@floating-ui/react'
import { motion, AnimatePresence } from 'framer-motion'
import { mergeRefs } from 'react-merge-refs'
import { arrow } from '@floating-ui/react-dom'

type Props = {
  label: string
  placement?: Placement
  children: JSX.Element
}

const Tooltip = ({ children, label, placement = 'top' }: Props): JSX.Element => {
  const [open, setOpen] = useState(false)
  const arrowRef = useRef(null)

  const { x, y, refs, strategy, context } = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    middleware: [
      offset(5),
      flip({ padding: 5 }),
      shift({ padding: 8 }),
      arrow({ element: arrowRef }),
    ],
    whileElementsMounted: autoUpdate,
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, { restMs: 40 }),
    useFocus(context),
    useRole(context, { role: 'tooltip' }),
    useDismiss(context),
  ])

  const ref = useMemo(
    () => mergeRefs([refs.setReference, (children as any).ref]),
    [refs, children],
  )

  return (
    <div>
      {cloneElement(children, getReferenceProps({ ref, ...children.props }))}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            ref={refs.setFloating}
            className={`${strategy} bg-foreground dark:bg-slate-300 shadow-lg rounded-md font-arial font-medium text-sm text-white p-2 dark:text-slate-800`}
            {...getFloatingProps({
              style: {
                position: strategy,
                top: y ?? 0,
                left: x ?? 0,
              },
            })}>
            {label}
            <FloatingArrow
              width={8}
              height={8}
              className="fill-foreground"
              context={context}
              ref={arrowRef}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Tooltip
